// src/pages/lms/LMSTimetable.jsx — COMPLETE FIXED VERSION
// Fixes: portal not showing (removed FK joins)
// New: teacher attendance marking, 10-min window, conflict detection,
//      teacher summary with stats, PIN management
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import LMSPageGuard from '../../components/lms/LMSPageGuard';
import { Plus, X, Trash2, AlertCircle, CheckCircle, Clock, RefreshCw, Users, Star, BarChart3 } from 'lucide-react';

const DAYS   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DAYS_S = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const COLORS = ['#4F8EF7','#7C3AED','#10B981','#F59E0B','#EF4444','#EC4899','#22D3EE','#8B5CF6','#F97316','#06B6D4'];
const TYPES  = ['lecture','lab','tutorial','seminar','workshop'];
const TYPE_COLORS = { lecture:'#4F8EF7', lab:'#10B981', tutorial:'#F59E0B', seminar:'#7C3AED', workshop:'#EF4444' };
const TIME_SLOTS = [
  '07:00','07:30','08:00','08:30','09:00','09:30','10:00','10:30',
  '11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30',
  '15:00','15:30','16:00','16:30','17:00','17:30','18:00','18:30',
  '19:00','19:30','20:00',
];
const INP = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box', outline:'none', color:'#111827' };
const F = ({ label, children, col }) => (
  <div style={{ gridColumn:col||'span 1' }}>
    <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>{label}</label>
    {children}
  </div>
);

// ── Teacher PIN Modal ──────────────────────────────────
function TeacherPinModal({ slot, institutionId, onSuccess, onClose }) {
  const [user,    setUser]    = useState('');
  const [pin,     setPin]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const submit = async () => {
    if (!user.trim() || !pin.trim()) { setError('Enter username and PIN'); return; }
    setLoading(true); setError('');
    try {
      const { data, error:e } = await supabase.rpc('verify_teacher_timetable_pin', {
        p_institution_id: institutionId,
        p_username:       user.trim(),
        p_pin:            pin.trim(),
        p_timetable_id:   slot.id,
      });
      if (e) throw e;
      if (!data.success) { setError(data.error||'Failed'); setLoading(false); return; }
      onSuccess(data);
    } catch(e) { setError(e.message||'Error'); setLoading(false); }
  };

  const now = new Date();
  const [sh, sm] = slot.start_time.split(':').map(Number);
  const slotStartMins = sh * 60 + sm;
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const windowEnd = slotStartMins + 10;
  const timeLeft = Math.max(0, windowEnd - nowMins);

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:400,padding:28}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
          <div>
            <h3 style={{margin:0,fontWeight:800,fontSize:18,color:'#111827'}}>Mark Attendance</h3>
            <div style={{fontSize:12,color:'#6B7280',marginTop:2}}>{slot.subject} · {slot.class_name}</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={20} color="#6B7280"/></button>
        </div>

        {/* Time window indicator */}
        <div style={{background:'#FFF7ED',border:'1px solid #FED7AA',borderRadius:12,padding:12,marginBottom:16,textAlign:'center'}}>
          <div style={{fontSize:13,fontWeight:700,color:'#92400E'}}>⏰ Attendance Window</div>
          <div style={{fontSize:22,fontWeight:900,color:'#F59E0B',marginTop:4}}>{slot.start_time} – {`${String(Math.floor(windowEnd/60)).padStart(2,'0')}:${String(windowEnd%60).padStart(2,'0')}`}</div>
          {timeLeft > 0
            ? <div style={{fontSize:11,color:'#B45309',marginTop:2}}>{timeLeft} minute{timeLeft!==1?'s':''} remaining</div>
            : <div style={{fontSize:11,color:'#EF4444',marginTop:2}}>Window may have closed — check timing</div>}
        </div>

        {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,padding:'10px',marginBottom:12,fontSize:13,color:'#991B1B',fontWeight:600}}>❌ {error}</div>}

        <div style={{marginBottom:12}}>
          <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>Your Username</label>
          <input value={user} onChange={e=>setUser(e.target.value)} placeholder="Teacher username" style={INP} autoFocus/>
        </div>
        <div style={{marginBottom:20}}>
          <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>Your PIN</label>
          <input value={pin} onChange={e=>setPin(e.target.value)} type="password" placeholder="••••" style={INP}
            onKeyDown={e=>e.key==='Enter'&&submit()}/>
        </div>

        <div style={{display:'flex',gap:10}}>
          <button onClick={onClose} style={{flex:1,padding:'12px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer'}}>Cancel</button>
          <button onClick={submit} disabled={loading}
            style={{flex:2,padding:'12px 0',borderRadius:10,border:'none',background:loading?'#E5E7EB':'linear-gradient(135deg,#10B981,#059669)',color:loading?'#9CA3AF':'#fff',fontWeight:700,cursor:loading?'wait':'pointer',fontSize:14}}>
            {loading ? 'Verifying...' : '✅ Mark Present'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Slot Form Modal ────────────────────────────────────
function SlotFormModal({ editSlot, selClass, institution, teachers, onSave, onClose }) {
  const [saving,    setSaving]    = useState(false);
  const [checking,  setChecking]  = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [error,     setError]     = useState('');
  const [form, setForm] = useState(editSlot ? {
    subject:       editSlot.subject||'',
    subject_code:  editSlot.subject_code||'',
    teacher_id:    editSlot.teacher_id||'',
    teacher_name:  editSlot.teacher_name||'',
    day_of_week:   editSlot.day_of_week,
    start_time:    editSlot.start_time,
    end_time:      editSlot.end_time,
    room:          editSlot.room||'',
    building:      editSlot.building||'',
    lecture_type:  editSlot.lecture_type||'lecture',
    color:         editSlot.color||'#4F8EF7',
    semester:      editSlot.semester||'',
    academic_year: editSlot.academic_year||'',
  } : {
    subject:'', subject_code:'', teacher_id:'', teacher_name:'',
    day_of_week:1, start_time:'08:00', end_time:'09:30',
    room:'', building:'', lecture_type:'lecture', color:'#4F8EF7',
    semester: selClass?.semester||'', academic_year: selClass?.academic_year||'',
  });

  const checkConflicts = async (f) => {
    if (!f.start_time || !f.end_time || !selClass) return;
    setChecking(true);
    try {
      const { data } = await supabase.rpc('check_timetable_conflicts', {
        p_institution_id: institution.id,
        p_class_id:       selClass.id,
        p_teacher_id:     f.teacher_id || null,
        p_room:           f.room || null,
        p_day_of_week:    Number(f.day_of_week),
        p_start_time:     f.start_time,
        p_end_time:       f.end_time,
        p_exclude_id:     editSlot?.id || null,
      });
      setConflicts(data || []);
    } catch(_) {}
    finally { setChecking(false); }
  };

  const handleTeacher = (tid) => {
    const t = teachers.find(t=>t.id===tid);
    const updated = {...form, teacher_id:tid, teacher_name:t?.full_name||''};
    setForm(updated);
    checkConflicts(updated);
  };

  const duration = (s,e) => {
    const [sh,sm]=s.split(':').map(Number); const [eh,em]=e.split(':').map(Number);
    const d=(eh*60+em)-(sh*60+sm);
    if(d<60)return`${d}min`; const h=Math.floor(d/60); const m=d%60;
    return m>0?`${h}h ${m}min`:`${h}h`;
  };

  const save = async () => {
    if (!form.subject.trim()) { setError('Subject is required'); return; }
    if (!form.start_time||!form.end_time) { setError('Times required'); return; }
    if (form.start_time>=form.end_time) { setError('End must be after start'); return; }
    if (conflicts.length>0) { setError('Resolve conflicts first'); return; }
    setSaving(true); setError('');
    try {
      const data = {
        institution_id:  institution.id,
        class_id:        selClass.id,
        class_name:      selClass.name,
        subject:         form.subject.trim(),
        subject_code:    form.subject_code.trim()||null,
        teacher_id:      form.teacher_id||null,
        teacher_name:    form.teacher_name||null,
        day_of_week:     Number(form.day_of_week),
        start_time:      form.start_time,
        end_time:        form.end_time,
        room:            form.room.trim()||null,
        building:        form.building.trim()||null,
        lecture_type:    form.lecture_type,
        color:           form.color,
        semester:        form.semester||selClass.semester||null,
        academic_year:   form.academic_year||selClass.academic_year||null,
        is_active:       true,
      };
      if (editSlot) {
        const { error:e } = await supabase.from('lms_timetable').update(data).eq('id', editSlot.id);
        if (e) throw e;
      } else {
        const { error:e } = await supabase.from('lms_timetable').insert(data);
        if (e) throw e;
        // Notify students of class about timetable update
        try {
          await supabase.functions.invoke('send-lms-notification', {
            body: {
              institution_id:    institution.id,
              class_id:          selClass.id,
              title:             '📅 Timetable Updated',
              body:              `${form.subject} added on ${DAYS[Number(form.day_of_week)]} at ${form.start_time}`,
              notification_type: 'timetable',
            }
          });
        } catch(_) {}
      }
      onSave(editSlot ? 'Slot updated!' : 'Slot added & students notified!');
    } catch(e) { setError(e.message||'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:580,maxHeight:'94vh',overflowY:'auto'}}>
        <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,background:'#fff',zIndex:1}}>
          <div>
            <h3 style={{margin:0,fontWeight:800,fontSize:18,color:'#111827'}}>{editSlot?'Edit Slot':'Add Slot'}</h3>
            <div style={{fontSize:12,color:'#6B7280',marginTop:2}}>Class: {selClass?.name}</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={22} color="#6B7280"/></button>
        </div>
        <div style={{padding:24}}>
          {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:13,color:'#991B1B',display:'flex',gap:8}}><AlertCircle size={14}/>{error}</div>}
          {conflicts.length>0 && (
            <div style={{background:'#FFF7ED',border:'1px solid #FED7AA',borderRadius:10,padding:14,marginBottom:14}}>
              <div style={{fontWeight:700,color:'#92400E',fontSize:13,marginBottom:8}}>⚠️ {conflicts.length} Conflict{conflicts.length>1?'s':''} Detected</div>
              {conflicts.map((c, i) => (
                <div key={i} style={{fontSize:12,color:'#92400E',marginBottom:4,paddingLeft:8,borderLeft:'2px solid #F59E0B'}}>
                  <strong>{c.type?.replace('_',' ')}:</strong> {c.message}
                </div>
              ))}
            </div>
          )}
          {conflicts.length===0&&form.subject&&form.start_time&&form.end_time&&(
            <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:8,padding:'10px 14px',marginBottom:14,display:'flex',alignItems:'center',gap:8,fontSize:13,color:'#065F46'}}>
              <CheckCircle size={14}/> No conflicts detected
            </div>
          )}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <F label="Day *">
              <select value={form.day_of_week} onChange={e=>{const f={...form,day_of_week:Number(e.target.value)};setForm(f);checkConflicts(f);}} style={INP}>
                {DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}
              </select>
            </F>
            <F label="Type">
              <select value={form.lecture_type} onChange={e=>setForm({...form,lecture_type:e.target.value,color:TYPE_COLORS[e.target.value]||form.color})} style={INP}>
                {TYPES.map(t=><option key={t} value={t}>{t[0].toUpperCase()+t.slice(1)}</option>)}
              </select>
            </F>
            <F label="Subject *" col="span 2">
              <input value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})}
                onBlur={()=>checkConflicts(form)} placeholder="e.g. Data Structures" style={INP}/>
            </F>
            <F label="Subject Code">
              <input value={form.subject_code} onChange={e=>setForm({...form,subject_code:e.target.value})} placeholder="e.g. CS-301" style={INP}/>
            </F>
            <F label="Teacher">
              <select value={form.teacher_id} onChange={e=>handleTeacher(e.target.value)} style={INP}>
                <option value="">No Teacher</option>
                {teachers.map(t=><option key={t.id} value={t.id}>{t.full_name}</option>)}
              </select>
            </F>
            <F label="Start Time *">
              <select value={form.start_time} onChange={e=>{const f={...form,start_time:e.target.value};setForm(f);checkConflicts(f);}} style={INP}>
                {TIME_SLOTS.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </F>
            <F label="End Time *">
              <select value={form.end_time} onChange={e=>{const f={...form,end_time:e.target.value};setForm(f);checkConflicts(f);}} style={INP}>
                {TIME_SLOTS.filter(t=>t>form.start_time).map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </F>
            {form.start_time&&form.end_time&&form.start_time<form.end_time&&(
              <div style={{gridColumn:'span 2',fontSize:12,color:'#10B981',fontWeight:600}}>
                ⏱ Duration: {duration(form.start_time,form.end_time)}
              </div>
            )}
            <F label="Room">
              <input value={form.room} onChange={e=>setForm({...form,room:e.target.value})}
                onBlur={()=>checkConflicts(form)} placeholder="Room 101" style={INP}/>
            </F>
            <F label="Building">
              <input value={form.building} onChange={e=>setForm({...form,building:e.target.value})} placeholder="Main Block" style={INP}/>
            </F>
            <F label="Color" col="span 2">
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {COLORS.map(c=>(
                  <div key={c} onClick={()=>setForm({...form,color:c})}
                    style={{width:28,height:28,borderRadius:'50%',background:c,cursor:'pointer',
                      border:form.color===c?'3px solid #111827':'3px solid transparent'}}/>
                ))}
              </div>
            </F>
          </div>
          <div style={{display:'flex',gap:10,marginTop:20}}>
            <button onClick={()=>checkConflicts(form)}
              style={{padding:'12px 14px',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer',fontSize:13,display:'flex',alignItems:'center',gap:6}}>
              {checking?'⏳':'🔍'} Check Conflicts
            </button>
            <button onClick={onClose} style={{flex:1,padding:'12px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer'}}>Cancel</button>
            <button onClick={save} disabled={saving||conflicts.length>0}
              style={{flex:2,padding:'12px 0',borderRadius:10,border:'none',background:conflicts.length>0?'#E2E8F0':'linear-gradient(135deg,#4F8EF7,#7C3AED)',color:conflicts.length>0?'#94A3B8':'#fff',fontWeight:700,cursor:saving||conflicts.length>0?'not-allowed':'pointer',opacity:saving?0.7:1}}>
              {saving?'Saving...':editSlot?'Update Slot':'Add to Timetable'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Teacher PIN Manager ────────────────────────────────
function PinManager({ teachers, institutionId, onClose }) {
  const [pins,   setPins]   = useState({});
  const [users,  setUsers]  = useState({});
  const [saving, setSaving] = useState({});
  const [done,   setDone]   = useState({});

  useEffect(() => {
    const pm={}, um={};
    for(const t of teachers){pm[t.id]=t.teacher_pin||''; um[t.id]=t.teacher_username||'';}
    setPins(pm); setUsers(um);
  }, [teachers]);

  const saveTeacher = async (tid) => {
    setSaving(s=>({...s,[tid]:true}));
    try {
      await supabase.from('lms_teachers').update({
        teacher_pin:      pins[tid]||null,
        teacher_username: users[tid]||null,
      }).eq('id',tid);
      setDone(d=>({...d,[tid]:true}));
      setTimeout(()=>setDone(d=>({...d,[tid]:false})),2000);
    } catch(e){} finally{setSaving(s=>({...s,[tid]:false}));}
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:600,maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,background:'#fff',zIndex:1}}>
          <div>
            <h3 style={{margin:0,fontWeight:800,fontSize:18,color:'#111827'}}>🔑 Teacher Attendance PINs</h3>
            <div style={{fontSize:12,color:'#6B7280',marginTop:2}}>Set username & PIN so teachers can mark lecture attendance</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={22} color="#6B7280"/></button>
        </div>
        <div style={{padding:20,display:'flex',flexDirection:'column',gap:12}}>
          {teachers.map(t=>(
            <div key={t.id} style={{background:'#F8FAFF',border:'1px solid #E2E8F0',borderRadius:14,padding:'14px 16px'}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
                {t.profile_photo_url
                  ? <img src={t.profile_photo_url} style={{width:38,height:38,borderRadius:'50%',objectFit:'cover'}} alt=""/>
                  : <div style={{width:38,height:38,borderRadius:'50%',background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:800,fontSize:16}}>{t.full_name[0]}</div>}
                <div>
                  <div style={{fontWeight:700,color:'#111827',fontSize:14}}>{t.full_name}</div>
                  <div style={{fontSize:11,color:'#6B7280'}}>{t.designation||'Teacher'} · {t.department||''}</div>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr auto',gap:8,alignItems:'end'}}>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:4}}>Username</label>
                  <input value={users[t.id]||''} onChange={e=>setUsers(u=>({...u,[t.id]:e.target.value}))}
                    placeholder="e.g. ahmed_teacher" style={{...INP,fontSize:12}}/>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:4}}>PIN (4-8 digits)</label>
                  <input value={pins[t.id]||''} type="password" onChange={e=>setPins(p=>({...p,[t.id]:e.target.value}))}
                    placeholder="••••" style={{...INP,fontSize:12}}/>
                </div>
                <button onClick={()=>saveTeacher(t.id)} disabled={saving[t.id]}
                  style={{padding:'9px 14px',borderRadius:8,border:'none',background:done[t.id]?'#10B981':saving[t.id]?'#E5E7EB':'#4F8EF7',color:'#fff',fontWeight:700,fontSize:12,cursor:'pointer',whiteSpace:'nowrap'}}>
                  {done[t.id]?'✅ Saved':saving[t.id]?'...':'Save'}
                </button>
              </div>
            </div>
          ))}
          {teachers.length===0 && <div style={{textAlign:'center',padding:32,color:'#9CA3AF'}}>No teachers found. Add teachers first.</div>}
        </div>
      </div>
    </div>
  );
}

// ── Teacher Summary ────────────────────────────────────
function TeacherSummary({ institutionId }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [attMap,  setAttMap]  = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [sumRes, attRes] = await Promise.all([
        supabase.rpc('get_teacher_timetable_summary', {p_institution_id: institutionId}).then(r=>r.data),
        supabase.rpc('get_timetable_attendance_status', {p_institution_id: institutionId}).then(r=>r.data),
      ]);
      setData(Array.isArray(sumRes) ? sumRes : []);
      const am = {};
      for(const s of (Array.isArray(attRes)?attRes:[])){ if(s.teacher_id) am[s.teacher_id]=(am[s.teacher_id]||0)+(s.marked?1:0); }
      setAttMap(am);
    } catch(e){console.error(e);}
    finally{setLoading(false);}
  };

  if(loading) return <div style={{padding:32,textAlign:'center',color:'#9CA3AF'}}>Loading summary...</div>;
  if(data.length===0) return <div style={{padding:32,textAlign:'center',color:'#9CA3AF'}}>No teacher data yet.</div>;

  const best = [...data].sort((a,b)=>(b.attendance_pct||0)-(a.attendance_pct||0))[0];

  return (
    <div>
      {/* Best performer */}
      {best && (
        <div style={{background:'linear-gradient(135deg,#F59E0B15,#EF444415)',border:'1px solid #FED7AA',borderRadius:16,padding:16,marginBottom:20,display:'flex',alignItems:'center',gap:14}}>
          <div style={{fontSize:32}}>🏆</div>
          <div>
            <div style={{fontSize:11,fontWeight:800,color:'#92400E',letterSpacing:0.5}}>TOP PERFORMER THIS MONTH</div>
            <div style={{fontSize:16,fontWeight:900,color:'#111827',marginTop:2}}>{best.teacher_name}</div>
            <div style={{fontSize:12,color:'#6B7280'}}>{best.attendance_pct}% attendance · {best.total_slots} weekly slots</div>
          </div>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:14}}>
        {data.map((t,i)=>{
          const pct = t.attendance_pct||0;
          const color = pct>=80?'#10B981':pct>=60?'#F59E0B':'#EF4444';
          const todayMarked = attMap[t.teacher_id]||0;
          return (
            <div key={i} style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
              {/* Header */}
              <div style={{padding:'14px 16px',background:pct>=80?'linear-gradient(135deg,#F0FDF4,#ECFDF5)':pct>=60?'linear-gradient(135deg,#FFF7ED,#FFFBEB)':'linear-gradient(135deg,#FEF2F2,#FFF5F5)',borderBottom:'1px solid #F1F5F9'}}>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  {t.profile_photo_url
                    ? <img src={t.profile_photo_url} style={{width:44,height:44,borderRadius:'50%',objectFit:'cover',border:'2px solid #fff'}} alt=""/>
                    : <div style={{width:44,height:44,borderRadius:'50%',background:`linear-gradient(135deg,${color},${color}99)`,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:800,fontSize:18}}>
                        {t.teacher_name?.[0]||'T'}
                      </div>}
                  <div style={{flex:1}}>
                    <div style={{fontWeight:800,color:'#111827',fontSize:14}}>{t.teacher_name}</div>
                    <div style={{fontSize:11,color:'#6B7280'}}>{t.designation||'Teacher'}</div>
                    {todayMarked>0 && <div style={{fontSize:10,color:'#10B981',fontWeight:700}}>✅ Attended today</div>}
                  </div>
                  <div style={{textAlign:'center'}}>
                    <div style={{fontSize:22,fontWeight:900,color}}>{pct}%</div>
                    <div style={{fontSize:9,color:'#9CA3AF',fontWeight:600}}>ATTEND.</div>
                  </div>
                </div>
              </div>
              {/* Stats */}
              <div style={{padding:'12px 16px'}}>
                {/* Progress bar */}
                <div style={{background:'#F1F5F9',borderRadius:6,height:6,overflow:'hidden',marginBottom:12}}>
                  <div style={{height:'100%',width:`${pct}%`,background:color,borderRadius:6,transition:'width 0.5s'}}/>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,textAlign:'center'}}>
                  {[
                    {l:'Weekly Slots', v:t.total_slots||0, c:'#4F8EF7'},
                    {l:'Attended',     v:t.attended||0,    c:'#10B981'},
                    {l:'Total Classes', v:t.total_scheduled||0, c:'#6B7280'},
                  ].map(s=>(
                    <div key={s.l} style={{background:'#F8FAFF',borderRadius:8,padding:'6px 4px'}}>
                      <div style={{fontSize:16,fontWeight:900,color:s.c}}>{s.v}</div>
                      <div style={{fontSize:9,color:'#9CA3AF',fontWeight:600}}>{s.l}</div>
                    </div>
                  ))}
                </div>
                {/* Subjects */}
                {t.subjects?.length>0 && (
                  <div style={{marginTop:10,display:'flex',flexWrap:'wrap',gap:4}}>
                    {t.subjects.slice(0,3).map((s,i)=>(
                      <span key={i} style={{fontSize:10,color:'#7C3AED',background:'#F5F3FF',padding:'2px 7px',borderRadius:4,fontWeight:600}}>{s}</span>
                    ))}
                    {t.subjects.length>3&&<span style={{fontSize:10,color:'#9CA3AF'}}>+{t.subjects.length-3} more</span>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ─────────────────────────────────────
export default function LMSTimetable() {
  const { institution } = useLMSAuth();
  const [classes,   setClasses]   = useState([]);
  const [teachers,  setTeachers]  = useState([]);
  const [slots,     setSlots]     = useState([]);
  const [attStatus, setAttStatus] = useState({});
  const [selClass,  setSelClass]  = useState(null);
  const [selDay,    setSelDay]    = useState(new Date().getDay());
  const [tab,       setTab]       = useState('timetable'); // timetable | summary
  const [viewMode,  setViewMode]  = useState('week');
  const [loading,   setLoading]   = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [showForm,  setShowForm]  = useState(false);
  const [editSlot,  setEditSlot]  = useState(null);
  const [showPin,   setShowPin]   = useState(null);
  const [showPinMgr,setShowPinMgr]= useState(false);
  const [pinResult, setPinResult] = useState(null);
  const [success,   setSuccess]   = useState('');
  const [error,     setError]     = useState('');

  useEffect(()=>{ if(institution?.id) loadInit(); },[institution?.id]);
  useEffect(()=>{ if(selClass) loadSlots(); },[selClass?.id]);

  const loadInit = async () => {
    setLoading(true);
    try {
      // NO FK joins — simple selects only
      const { data:cData } = await supabase.from('lms_classes').select('*')
        .eq('institution_id', institution.id).eq('is_active',true).order('name');
      const { data:tData } = await supabase.from('lms_teachers')
        .select('id,full_name,designation,department,profile_photo_url,teacher_pin,teacher_username,subjects')
        .eq('institution_id', institution.id).eq('status','active').order('full_name');
      setClasses(cData||[]);
      setTeachers(tData||[]);
      if(cData?.length>0) setSelClass(cData[0]);
    } catch(e){ console.error(e); }
    finally{ setLoading(false); }
  };

  const loadSlots = async () => {
    if(!selClass) return;
    setLoadingSlots(true);
    try {
      // NO FK joins — simple select
      const { data, error:e } = await supabase.from('lms_timetable').select('*')
        .eq('class_id', selClass.id).eq('is_active',true)
        .order('day_of_week').order('start_time');
      if(e) throw e;
      setSlots(data||[]);

      // Load today's attendance status
      const today = new Date().getDay();
      const todaySlots = (data||[]).filter(s=>s.day_of_week===today);
      if(todaySlots.length>0){
        const { data:attData } = await supabase.from('lms_teacher_attendance')
          .select('timetable_id,marked_at')
          .in('timetable_id', todaySlots.map(s=>s.id))
          .eq('scheduled_date', new Date().toISOString().split('T')[0]);
        const am={};
        for(const a of (attData||[])) am[a.timetable_id]=a.marked_at;
        setAttStatus(am);
      }
    } catch(e){ console.error('Slots load error:',e); setError('Failed to load timetable: '+e.message); }
    finally{ setLoadingSlots(false); }
  };

  const deleteSlot = async (id) => {
    if(!window.confirm('Remove this slot?')) return;
    await supabase.from('lms_timetable').update({is_active:false}).eq('id',id);
    setSlots(prev=>prev.filter(s=>s.id!==id));
    setSuccess('Slot removed'); setTimeout(()=>setSuccess(''),2000);
  };

  const onSaved = async (msg) => {
    setSuccess(msg); setShowForm(false); setEditSlot(null);
    setTimeout(()=>setSuccess(''),4000);
    await loadSlots();
  };

  const daySlots = (day) => slots.filter(s=>s.day_of_week===day)
    .sort((a,b)=>a.start_time.localeCompare(b.start_time));

  const isInWindow = (slot) => {
    const today = new Date().getDay();
    if(slot.day_of_week!==today) return false;
    const now=new Date();
    const [sh,sm]=slot.start_time.split(':').map(Number);
    const nowMins=now.getHours()*60+now.getMinutes();
    const startMins=sh*60+sm;
    return nowMins>=startMins && nowMins<=(startMins+10);
  };

  const duration = (s,e) => {
    const [sh,sm]=s.split(':').map(Number); const [eh,em]=e.split(':').map(Number);
    const d=(eh*60+em)-(sh*60+sm);
    if(d<60)return`${d}min`; const h=Math.floor(d/60); const m=d%60;
    return m>0?`${h}h ${m}min`:`${h}h`;
  };

  return (
    <LMSLayout>
      <LMSPageGuard pageKey="timetable">

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
          <div>
            <h1 style={{fontSize:22,fontWeight:800,color:'#111827',margin:0}}>Timetable</h1>
            <p style={{color:'#6B7280',fontSize:13,margin:'4px 0 0'}}>Build schedules · Track teacher attendance</p>
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <button onClick={()=>setShowPinMgr(true)}
              style={{display:'flex',alignItems:'center',gap:6,padding:'9px 14px',borderRadius:9,border:'1px solid #E5E7EB',background:'#fff',color:'#374151',fontSize:13,fontWeight:600,cursor:'pointer'}}>
              🔑 Manage PINs
            </button>
            <button onClick={loadInit}
              style={{display:'flex',alignItems:'center',gap:6,padding:'9px 14px',borderRadius:9,border:'1px solid #E5E7EB',background:'#fff',color:'#374151',fontSize:13,fontWeight:600,cursor:'pointer'}}>
              <RefreshCw size={14}/> Refresh
            </button>
            {tab==='timetable'&&selClass&&(
              <button onClick={()=>{setEditSlot(null);setShowForm(true);}}
                style={{display:'flex',alignItems:'center',gap:8,padding:'10px 18px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                <Plus size={16}/> Add Slot
              </button>
            )}
          </div>
        </div>

        {success&&<div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#065F46',fontWeight:700}}>{success}</div>}
        {error  &&<div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#991B1B',display:'flex',gap:8,alignItems:'center'}}><AlertCircle size={14}/>{error}<button onClick={()=>setError('')} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer'}}>✕</button></div>}

        {/* Tabs */}
        <div style={{display:'flex',background:'#F1F5F9',borderRadius:12,padding:4,marginBottom:20,width:'fit-content',gap:2}}>
          {[{k:'timetable',l:'📅 Timetable'},{k:'summary',l:'📊 Teacher Summary'}].map(t=>(
            <button key={t.k} onClick={()=>setTab(t.k)}
              style={{padding:'8px 18px',borderRadius:9,border:'none',background:tab===t.k?'#fff':'transparent',color:tab===t.k?'#1E293B':'#64748B',fontWeight:700,fontSize:13,cursor:'pointer',boxShadow:tab===t.k?'0 1px 4px rgba(0,0,0,0.08)':'none'}}>
              {t.l}
            </button>
          ))}
        </div>

        {/* ── TEACHER SUMMARY TAB ── */}
        {tab==='summary' && institution?.id && <TeacherSummary institutionId={institution.id}/>}

        {/* ── TIMETABLE TAB ── */}
        {tab==='timetable' && (
          <>
            {/* Class selector */}
            <div style={{background:'#fff',borderRadius:14,border:'1px solid #E5E7EB',padding:16,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:'#374151',marginBottom:10}}>Select Class</div>
              {classes.length===0
                ? <div style={{fontSize:13,color:'#9CA3AF'}}>No classes. Create classes first.</div>
                : <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                    {classes.map(cls=>(
                      <button key={cls.id} onClick={()=>setSelClass(cls)}
                        style={{padding:'8px 16px',borderRadius:10,border:`2px solid ${selClass?.id===cls.id?'#4F8EF7':'#E5E7EB'}`,background:selClass?.id===cls.id?'#EFF6FF':'#fff',color:selClass?.id===cls.id?'#1D4ED8':'#374151',fontWeight:selClass?.id===cls.id?700:500,fontSize:13,cursor:'pointer'}}>
                        {cls.name}
                        {cls.semester&&<span style={{fontSize:10,color:selClass?.id===cls.id?'#3B82F6':'#9CA3AF',marginLeft:5}}>{cls.semester}</span>}
                      </button>
                    ))}
                  </div>}
            </div>

            {selClass && (
              <>
                {/* Class info + view toggle */}
                <div style={{background:'linear-gradient(135deg,#4F8EF710,#7C3AED10)',border:'1px solid #E2E8F0',borderRadius:12,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                  <div style={{fontWeight:800,color:'#1E293B',fontSize:15}}>{selClass.name}</div>
                  {selClass.display_name&&<div style={{fontSize:12,color:'#6B7280'}}>{selClass.display_name}</div>}
                  <div style={{display:'flex',gap:6,marginLeft:'auto',flexWrap:'wrap',alignItems:'center'}}>
                    <span style={{fontSize:11,color:'#10B981',background:'#F0FDF4',padding:'2px 8px',borderRadius:4,fontWeight:600}}>{slots.length} slots</span>
                    <div style={{display:'flex',background:'#F1F5F9',borderRadius:8,padding:2}}>
                      {['week','day'].map(m=>(
                        <button key={m} onClick={()=>setViewMode(m)}
                          style={{padding:'5px 12px',borderRadius:7,border:'none',background:viewMode===m?'#fff':'transparent',color:viewMode===m?'#1E293B':'#64748B',fontWeight:700,fontSize:11,cursor:'pointer',boxShadow:viewMode===m?'0 1px 3px rgba(0,0,0,0.08)':'none'}}>
                          {m==='week'?'Week':'Day'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Day tabs (day view) */}
                {viewMode==='day'&&(
                  <div style={{display:'flex',gap:6,marginBottom:16,overflowX:'auto',paddingBottom:4}}>
                    {DAYS.map((day,i)=>{
                      const cnt=daySlots(i).length;
                      return(
                        <button key={i} onClick={()=>setSelDay(i)}
                          style={{padding:'8px 14px',borderRadius:10,border:`2px solid ${selDay===i?'#4F8EF7':'#E5E7EB'}`,background:selDay===i?'#EFF6FF':'#fff',color:selDay===i?'#1D4ED8':'#374151',fontWeight:700,fontSize:12,cursor:'pointer',whiteSpace:'nowrap',minWidth:58,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                          {DAYS_S[i]}
                          {cnt>0&&<span style={{fontSize:10,color:selDay===i?'#3B82F6':'#9CA3AF'}}>{cnt}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {loadingSlots ? (
                  <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>Loading...</div>
                ) : viewMode==='week' ? (

                  // ── WEEK VIEW ────────────────────────
                  <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:8}}>
                    {DAYS.map((day,i)=>{
                      const ds=daySlots(i);
                      const isToday=i===new Date().getDay();
                      return(
                        <div key={i} style={{background:'#fff',borderRadius:12,border:`1px solid ${isToday?'#BFDBFE':'#E5E7EB'}`,overflow:'hidden',minHeight:120}}>
                          <div style={{padding:'8px 10px',background:isToday?'#EFF6FF':ds.length>0?'linear-gradient(135deg,#4F8EF720,#7C3AED10)':'#F9FAFB',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                            <span style={{fontSize:12,fontWeight:700,color:isToday?'#1D4ED8':ds.length>0?'#1E293B':'#9CA3AF'}}>{DAYS_S[i]}{isToday&&' •'}</span>
                            <button onClick={()=>{setSelDay(i);setEditSlot(null);setShowForm(true);}}
                              style={{width:20,height:20,borderRadius:'50%',border:'none',background:'#4F8EF720',color:'#4F8EF7',cursor:'pointer',fontSize:14,lineHeight:1,display:'flex',alignItems:'center',justifyContent:'center'}}>+</button>
                          </div>
                          <div style={{padding:6}}>
                            {ds.length===0
                              ? <div style={{padding:'8px 4px',textAlign:'center',fontSize:10,color:'#D1D5DB'}}>Free</div>
                              : ds.map(slot=>{
                                  const marked=!!attStatus[slot.id];
                                  const inWindow=isInWindow(slot);
                                  return(
                                    <div key={slot.id} style={{marginBottom:4,padding:'6px 8px',borderRadius:7,background:`${slot.color}15`,border:`1px solid ${slot.color}40`,cursor:'pointer'}}
                                      onClick={()=>{setEditSlot(slot);setShowForm(true);}}>
                                      <div style={{fontSize:10,fontWeight:800,color:slot.color,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{slot.subject}</div>
                                      <div style={{fontSize:9,color:'#64748B'}}>{slot.start_time}–{slot.end_time}</div>
                                      {slot.teacher_name&&<div style={{fontSize:9,color:'#94A3B8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{slot.teacher_name}</div>}
                                      {/* Attendance badge */}
                                      {slot.teacher_id&&(
                                        <div style={{marginTop:4}}>
                                          {marked
                                            ? <span style={{fontSize:8,fontWeight:800,color:'#10B981',background:'#F0FDF4',padding:'1px 5px',borderRadius:3}}>✅ Attended</span>
                                            : inWindow
                                            ? <button onClick={e=>{e.stopPropagation();setShowPin(slot);}}
                                                style={{fontSize:8,fontWeight:800,color:'#fff',background:'#F59E0B',padding:'2px 5px',borderRadius:3,border:'none',cursor:'pointer',width:'100%'}}>
                                                Mark Attendance
                                              </button>
                                            : <span style={{fontSize:8,color:'#9CA3AF'}}>—</span>}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                ) : (

                  // ── DAY VIEW ─────────────────────────
                  <div style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',overflow:'hidden'}}>
                    <div style={{padding:'14px 20px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                      <div style={{fontWeight:800,color:'#1E293B',fontSize:16}}>{DAYS[selDay]}</div>
                      <div style={{fontSize:13,color:'#6B7280'}}>{daySlots(selDay).length} slots</div>
                    </div>
                    {daySlots(selDay).length===0 ? (
                      <div style={{padding:48,textAlign:'center'}}>
                        <Clock size={36} color="#E5E7EB" style={{marginBottom:12}}/>
                        <div style={{fontSize:14,fontWeight:600,color:'#374151',marginBottom:8}}>No classes on {DAYS[selDay]}</div>
                        <button onClick={()=>{setEditSlot(null);setShowForm(true);}}
                          style={{padding:'8px 20px',borderRadius:8,border:'none',background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer'}}>
                          + Add Lecture
                        </button>
                      </div>
                    ) : (
                      <div style={{padding:16,display:'flex',flexDirection:'column',gap:10}}>
                        {daySlots(selDay).map(slot=>{
                          const marked=!!attStatus[slot.id];
                          const inWindow=isInWindow(slot);
                          return(
                            <div key={slot.id} style={{display:'flex',gap:0,alignItems:'stretch'}}>
                              <div style={{width:80,flexShrink:0,display:'flex',flexDirection:'column',alignItems:'center',paddingRight:12}}>
                                <div style={{fontSize:13,fontWeight:800,color:slot.color}}>{slot.start_time}</div>
                                <div style={{flex:1,width:2,background:`${slot.color}30`,margin:'4px 0'}}/>
                                <div style={{fontSize:11,color:'#94A3B8'}}>{slot.end_time}</div>
                              </div>
                              <div style={{flex:1,padding:'14px 16px',borderRadius:14,background:`${slot.color}10`,border:`1.5px solid ${slot.color}40`}}>
                                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
                                  <div style={{flex:1}}>
                                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                                      <span style={{fontSize:11,fontWeight:800,color:slot.color,background:`${slot.color}20`,padding:'2px 8px',borderRadius:4,textTransform:'uppercase'}}>{slot.lecture_type}</span>
                                      {slot.subject_code&&<span style={{fontSize:11,color:'#94A3B8'}}>{slot.subject_code}</span>}
                                      <span style={{fontSize:11,color:'#94A3B8'}}>{duration(slot.start_time,slot.end_time)}</span>
                                      {marked&&<span style={{fontSize:10,fontWeight:800,color:'#10B981',background:'#F0FDF4',padding:'2px 7px',borderRadius:4}}>✅ Attended</span>}
                                    </div>
                                    <div style={{fontSize:16,fontWeight:800,color:'#1E293B',marginBottom:4}}>{slot.subject}</div>
                                    <div style={{display:'flex',gap:12,fontSize:12,color:'#6B7280',flexWrap:'wrap'}}>
                                      {slot.teacher_name&&<span>👤 {slot.teacher_name}</span>}
                                      {slot.room&&<span>📍 {slot.room}{slot.building?`, ${slot.building}`:''}</span>}
                                    </div>
                                    {/* Mark attendance button */}
                                    {slot.teacher_id&&!marked&&inWindow&&(
                                      <button onClick={()=>setShowPin(slot)}
                                        style={{marginTop:10,padding:'7px 14px',borderRadius:8,border:'none',background:'#F59E0B',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                                        ⏰ Mark My Attendance
                                      </button>
                                    )}
                                    {slot.teacher_id&&!marked&&!inWindow&&selDay===new Date().getDay()&&(
                                      <div style={{marginTop:6,fontSize:11,color:'#9CA3AF'}}>
                                        Window: {slot.start_time} to {(() => { const [h,m]=slot.start_time.split(':').map(Number); const end=h*60+m+10; return `${String(Math.floor(end/60)).padStart(2,'0')}:${String(end%60).padStart(2,'0')}`; })()}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{display:'flex',gap:6,flexShrink:0}}>
                                    <button onClick={()=>{setEditSlot(slot);setShowForm(true);}}
                                      style={{padding:'6px 10px',borderRadius:7,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',fontSize:12,fontWeight:600,color:'#374151'}}>Edit</button>
                                    <button onClick={()=>deleteSlot(slot.id)}
                                      style={{padding:'6px 10px',borderRadius:7,border:'1px solid #FECACA',background:'#FEF2F2',cursor:'pointer'}}>
                                      <Trash2 size={13} color="#EF4444"/>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <button onClick={()=>{setEditSlot(null);setShowForm(true);}}
                          style={{padding:'10px 0',borderRadius:10,border:'2px dashed #E5E7EB',background:'transparent',color:'#94A3B8',fontWeight:600,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                          <Plus size={14}/> Add Another Lecture
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Pin result success */}
        {pinResult && (
          <div style={{position:'fixed',bottom:24,right:24,background:'#10B981',color:'#fff',borderRadius:16,padding:'16px 20px',boxShadow:'0 8px 24px rgba(0,0,0,0.15)',zIndex:2000,display:'flex',alignItems:'center',gap:12,maxWidth:360}}>
            <span style={{fontSize:24}}>✅</span>
            <div>
              <div style={{fontWeight:800,fontSize:15}}>Attendance Marked!</div>
              <div style={{fontSize:12,opacity:0.9}}>{pinResult.teacher_name} · {pinResult.subject}</div>
            </div>
            <button onClick={()=>setPinResult(null)} style={{background:'none',border:'none',color:'#fff',cursor:'pointer',marginLeft:'auto'}}><X size={16}/></button>
          </div>
        )}

        {/* Modals */}
        {showForm && selClass && (
          <SlotFormModal
            key={editSlot?.id||'new'}
            editSlot={editSlot}
            selClass={selClass}
            institution={institution}
            teachers={teachers}
            onSave={onSaved}
            onClose={()=>{setShowForm(false);setEditSlot(null);}}/>
        )}
        {showPin && (
          <TeacherPinModal
            slot={showPin}
            institutionId={institution?.id}
            onSuccess={(r)=>{
              setPinResult(r); setShowPin(null);
              loadSlots(); // refresh attendance badges
            }}
            onClose={()=>setShowPin(null)}/>
        )}
        {showPinMgr && (
          <PinManager
            teachers={teachers}
            institutionId={institution?.id}
            onClose={()=>{setShowPinMgr(false);loadInit();}}/>
        )}

      </LMSPageGuard>
    </LMSLayout>
  );
}